// ============================================================================
// RHFormsPrimeiro15_Relatorio.js
// Relatório dos "Primeiros 15 dias" reformulado (padrão pontuado).
// Lê GET /api/forms-rh/primeiro15/relatorio-v2 → { linhas, notas }.
//
// A NOTA (1 a 5 por seção: % de "Sim" + escala, normalizados numa régua comum) e o NPS são
// calculados no BACKEND (server/rh/primeiro15-score.js, testado) e recalculados
// lá quando os filtros mudam — o front só exibe. Distribuições das seleções e os
// feedbacks (texto) são montados aqui a partir das linhas. Relatório ANÔNIMO.
// ============================================================================

import { useEffect, useState, useMemo, useCallback } from 'react';
import Sidebar from '../../Sidebar';
import { Filter } from 'lucide-react';
import { TODAS_PERGUNTAS } from './primeiro15-perguntas';

const MESES = [
  { v: '1', nome: 'Janeiro' }, { v: '2', nome: 'Fevereiro' }, { v: '3', nome: 'Março' },
  { v: '4', nome: 'Abril' }, { v: '5', nome: 'Maio' }, { v: '6', nome: 'Junho' },
  { v: '7', nome: 'Julho' }, { v: '8', nome: 'Agosto' }, { v: '9', nome: 'Setembro' },
  { v: '10', nome: 'Outubro' }, { v: '11', nome: 'Novembro' }, { v: '12', nome: 'Dezembro' },
];

const SELECOES = TODAS_PERGUNTAS.filter((p) => p.tipo === 'selecao');
const CAMPOS_TEXTO = TODAS_PERGUNTAS.filter((p) => p.tipo === 'texto');

// Nota 1-5 (satisfação): verde ≥4,2, âmbar ≥3,4, vermelho <3,4 — os mesmos
// cortes de antes (80 e 60 na régua 0-100), só convertidos para a escala nova.
const corIndice = (v) => (v == null ? '#94a3b8' : v >= 4.2 ? '#10b981' : v >= 3.4 ? '#f59e0b' : '#ef4444');
// % favorável de uma pergunta Sim/Não (segue em 0-100 no detalhe por pergunta —
// só a NOTA agregada virou 1-5): verde ≥80, âmbar ≥60, vermelho <60.
const corPct = (v) => (v == null ? '#94a3b8' : v >= 80 ? '#10b981' : v >= 60 ? '#f59e0b' : '#ef4444');
// Média 1-5 (perguntas de escala): verde ≥4, âmbar ≥3, vermelho <3.
const corMedia = (v) => (v == null ? '#94a3b8' : v >= 4 ? '#10b981' : v >= 3 ? '#f59e0b' : '#ef4444');
// NPS (−100..+100): verde ≥50, âmbar ≥0, vermelho <0.
const corNps = (v) => (v == null ? '#94a3b8' : v >= 50 ? '#10b981' : v >= 0 ? '#f59e0b' : '#ef4444');

function Primeiro15Relatorio({ embedded = false }) {
  const [isCollapsed, setIsCollapsed] = useState(window.innerWidth <= 768);
  const getMarginLeft = () => {
    if (window.innerWidth <= 480) return isCollapsed ? '60px' : '0px';
    return isCollapsed ? '100px' : '260px';
  };
  useEffect(() => {
    const onResize = () => { if (window.innerWidth <= 768) setIsCollapsed(true); };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const [linhas, setLinhas] = useState([]);
  const [notas, setNotas] = useState({ geral: null, secoes: [], nps: null, totalRespostas: 0 });
  const [todasLojas, setTodasLojas] = useState([]);
  const [anosDisponiveis, setAnosDisponiveis] = useState([]);
  const [filterLoja, setFilterLoja] = useState('');
  const [filterAno, setFilterAno] = useState('');
  const [filterMes, setFilterMes] = useState('');
  const [carregando, setCarregando] = useState(false);

  const buscar = useCallback(async ({ loja = '', inicio = '', fim = '', primeiraCarga = false } = {}) => {
    setCarregando(true);
    try {
      const token = localStorage.getItem('authToken');
      if (!token) throw new Error('Token não encontrado. Faça o login novamente.');
      const qs = new URLSearchParams();
      if (loja) qs.set('loja', loja);
      if (inicio) qs.set('inicio', inicio);
      if (fim) qs.set('fim', fim);
      const url = `/api/forms-rh/primeiro15/relatorio-v2${qs.toString() ? `?${qs}` : ''}`;
      const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) {
        let msg = `Status: ${res.status} ${res.statusText}`;
        try { const j = await res.json(); msg = j.message || JSON.stringify(j); } catch (e) { /* sem JSON */ }
        throw new Error(msg);
      }
      const data = await res.json();
      const linhasFmt = (data.linhas || []).map((row) => {
        let d = row.DATA;
        if (row.DATA) { try { d = new Date(row.DATA).toISOString().split('T')[0]; } catch (e) { /* mantém */ } }
        return { ...row, DATA: d };
      });
      setLinhas(linhasFmt);
      setNotas(data.notas || { geral: null, secoes: [], nps: null, totalRespostas: 0 });
      if (primeiraCarga) {
        setTodasLojas([...new Set(linhasFmt.map((r) => r.LOJA).filter(Boolean))].sort());
        setAnosDisponiveis([...new Set(linhasFmt.map((r) => (r.DATA || '').slice(0, 4)).filter(Boolean))].sort().reverse());
      }
    } catch (error) {
      console.error('Erro ao buscar relatório 15 dias:', error.message);
      alert(`Erro ao buscar relatório: ${error.message}`);
    } finally {
      setCarregando(false);
    }
  }, []);

  useEffect(() => { buscar({ primeiraCarga: true }); }, [buscar]);

  const aplicarFiltros = () => {
    let inicio = ''; let fim = '';
    if (filterAno) {
      if (filterMes) {
        const mm = String(filterMes).padStart(2, '0');
        const ultimoDia = new Date(Number(filterAno), Number(filterMes), 0).getDate();
        inicio = `${filterAno}-${mm}-01`;
        fim = `${filterAno}-${mm}-${String(ultimoDia).padStart(2, '0')}`;
      } else {
        inicio = `${filterAno}-01-01`;
        fim = `${filterAno}-12-31`;
      }
    }
    buscar({ loja: filterLoja, inicio, fim });
  };

  // Distribuição das perguntas de seleção (contagem por opção).
  const distribuicoes = useMemo(() => SELECOES.map((p) => {
    const total = linhas.length || 1;
    const cont = {};
    p.opcoes.forEach((o) => { cont[o] = 0; });
    linhas.forEach((r) => { if (r[p.id] && cont[r[p.id]] !== undefined) cont[r[p.id]]++; });
    return {
      id: p.id, texto: p.texto,
      itens: p.opcoes.map((o) => ({ opcao: o, qtd: cont[o], pct: Math.round((cont[o] / total) * 100) })).sort((a, b) => b.qtd - a.qtd),
    };
  }), [linhas]);

  // Feedbacks (texto livre) preenchidos.
  const feedbacks = useMemo(
    () => linhas.filter((r) => CAMPOS_TEXTO.some((c) => (r[c.id] || '').trim() !== '')),
    [linhas]
  );

  const CardNota = ({ titulo, valor, cor, sufixo = '', destaque = false }) => (
    <div style={{
      backgroundColor: '#fff', border: destaque ? '2px solid #e11d48' : '1px solid #e2e8f0', borderRadius: '14px', padding: '16px',
      display: 'flex', flexDirection: 'column', justifyContent: 'space-between', gap: '10px', height: '100%', minHeight: '104px', boxSizing: 'border-box',
    }}>
      <span style={{ fontSize: '12px', fontWeight: 600, color: '#64748b', lineHeight: 1.3, wordBreak: 'break-word' }}>{titulo}</span>
      <span style={{ fontSize: '28px', fontWeight: 800, color: cor, fontVariantNumeric: 'tabular-nums' }}>
        {valor == null ? '—' : `${valor}${sufixo}`}
      </span>
    </div>
  );

  return (
    <div style={embedded ? { width: '100%' } : { display: 'flex', minHeight: '100vh', backgroundColor: '#f8fafc' }}>
      {!embedded && <Sidebar isCollapsed={isCollapsed} setIsCollapsed={setIsCollapsed} />}
      <div style={{ flex: 1, marginLeft: embedded ? 0 : getMarginLeft(), transition: 'margin-left 0.3s ease' }}>
        {!embedded && (
          <div style={{ height: '80px', backgroundColor: '#fff', borderBottom: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', padding: '0 40px', position: 'sticky', top: 0, zIndex: 10 }}>
            <h2 style={{ margin: 0, fontSize: '20px', fontWeight: '700', color: '#0f172a' }}>Relatório — Primeiros 15 dias</h2>
          </div>
        )}

        <div style={{ padding: '40px', maxWidth: '1200px', margin: '0 auto' }}>
          <div style={{ marginBottom: '24px' }}>
            <h1 style={{ fontSize: '28px', fontWeight: '800', color: '#0f172a', marginBottom: '8px' }}>Relatório — Primeiros 15 dias</h1>
            <p style={{ color: '#64748b', fontSize: '16px' }}>Nota de satisfação (1 a 5) por seção, NPS, distribuições e sugestões (anônimo).</p>
          </div>

          {/* FILTROS */}
          <div style={{ backgroundColor: '#fff', padding: '24px', borderRadius: '16px', border: '1px solid #e2e8f0', display: 'flex', flexWrap: 'wrap', gap: '20px', marginBottom: '24px', alignItems: 'flex-end' }}>
            <div style={{ flex: '1 1 200px' }}>
              <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#475569', marginBottom: '8px' }}>Loja</label>
              <select value={filterLoja} onChange={(e) => setFilterLoja(e.target.value)} style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #cbd5e1', outline: 'none' }}>
                <option value="">Todas</option>
                {todasLojas.map((l) => <option key={l} value={l}>{l}</option>)}
              </select>
            </div>
            <div style={{ flex: '1 1 120px' }}>
              <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#475569', marginBottom: '8px' }}>Ano</label>
              <select value={filterAno} onChange={(e) => setFilterAno(e.target.value)} style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #cbd5e1', outline: 'none' }}>
                <option value="">Todos</option>
                {anosDisponiveis.map((a) => <option key={a} value={a}>{a}</option>)}
              </select>
            </div>
            <div style={{ flex: '1 1 150px' }}>
              <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#475569', marginBottom: '8px' }}>Mês</label>
              <select value={filterMes} onChange={(e) => setFilterMes(e.target.value)} disabled={!filterAno} style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #cbd5e1', outline: 'none', backgroundColor: filterAno ? '#fff' : '#f1f5f9' }}>
                <option value="">Todos</option>
                {MESES.map((m) => <option key={m.v} value={m.v}>{m.nome}</option>)}
              </select>
            </div>
            <button onClick={aplicarFiltros} disabled={carregando} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 20px', backgroundColor: '#0f172a', color: '#fff', border: 'none', borderRadius: '8px', fontWeight: '600', cursor: 'pointer', opacity: carregando ? 0.6 : 1 }}>
              <Filter size={16} /> Aplicar Filtros
            </button>
          </div>

          {/* NOTAS (1 a 5) + NPS — todos na mesma linha */}
          <div style={{ display: 'grid', gridTemplateColumns: `repeat(${notas.secoes.length + 2}, minmax(0, 1fr))`, gap: '12px', marginBottom: '16px' }}>
            <CardNota titulo={`Nota geral (${notas.totalRespostas} resposta${notas.totalRespostas === 1 ? '' : 's'})`} valor={notas.geral} cor={corIndice(notas.geral)} sufixo="/5" destaque />
            {notas.secoes.map((s) => <CardNota key={s.id} titulo={`${s.id}. ${s.titulo}`} valor={s.nota} cor={corIndice(s.nota)} sufixo="/5" />)}
            <CardNota titulo={`NPS (${notas.nps ? notas.nps.n : 0} nota${notas.nps && notas.nps.n === 1 ? '' : 's'})`} valor={notas.nps ? notas.nps.valor : null} cor={corNps(notas.nps ? notas.nps.valor : null)} />
          </div>

          {/* NPS detalhado */}
          {notas.nps && (
            <div style={{ backgroundColor: '#fff', border: '1px solid #e2e8f0', borderRadius: '16px', padding: '20px 24px', marginBottom: '24px', display: 'flex', gap: '24px', flexWrap: 'wrap' }}>
              <span style={{ fontSize: '13px', color: '#475569' }}>Promotores (9-10): <strong style={{ color: '#10b981', fontVariantNumeric: 'tabular-nums' }}>{notas.nps.promotores}</strong></span>
              <span style={{ fontSize: '13px', color: '#475569' }}>Passivos (7-8): <strong style={{ color: '#f59e0b', fontVariantNumeric: 'tabular-nums' }}>{notas.nps.passivos}</strong></span>
              <span style={{ fontSize: '13px', color: '#475569' }}>Detratores (0-6): <strong style={{ color: '#ef4444', fontVariantNumeric: 'tabular-nums' }}>{notas.nps.detratores}</strong></span>
            </div>
          )}

          {/* DETALHE POR PERGUNTA (seções pontuadas) */}
          <div style={{ backgroundColor: '#fff', border: '1px solid #e2e8f0', borderRadius: '16px', padding: '24px', marginBottom: '24px' }}>
            <h3 style={{ margin: '0 0 16px 0', fontSize: '16px', fontWeight: 700, color: '#0f172a' }}>Detalhe por pergunta</h3>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '16px' }}>
              {notas.secoes.map((s) => (
                <div key={s.id} style={{ border: '1px solid #f1f5f9', borderRadius: '12px', padding: '14px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                    <span style={{ fontSize: '13px', fontWeight: 700, color: '#334155' }}>{s.id}. {s.titulo}</span>
                    <span style={{ fontSize: '15px', fontWeight: 800, color: corIndice(s.nota), fontVariantNumeric: 'tabular-nums' }}>{s.nota == null ? '—' : `${s.nota}/5`}</span>
                  </div>
                  {s.perguntas.map((p) => {
                    const ehEscala = p.tipo === 'escala';
                    const valor = !p.agg ? null : ehEscala ? p.agg.media : p.agg.pctPositivo;
                    const texto = valor == null ? '—' : ehEscala ? valor.toFixed(2) : `${valor}%`;
                    const cor = !p.agg ? '#94a3b8' : ehEscala ? corMedia(p.agg.media) : corPct(p.agg.pctPositivo);
                    return (
                      <div key={p.id} style={{ display: 'flex', justifyContent: 'space-between', gap: '10px', fontSize: '12px', color: '#64748b', padding: '3px 0' }}>
                        <span style={{ flex: 1 }}>{p.texto} <span style={{ color: '#cbd5e1' }}>{ehEscala ? '(1-5)' : '(% favorável)'}</span></span>
                        <span style={{ fontWeight: 700, color: cor, fontVariantNumeric: 'tabular-nums' }}>{texto}</span>
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>
          </div>

          {/* DISTRIBUIÇÕES (seleções informativas) */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '16px', marginBottom: '24px' }}>
            {distribuicoes.map((d) => (
              <div key={d.id} style={{ backgroundColor: '#fff', border: '1px solid #e2e8f0', borderRadius: '16px', padding: '24px' }}>
                <h3 style={{ margin: '0 0 16px 0', fontSize: '15px', fontWeight: 700, color: '#0f172a' }}>{d.texto}</h3>
                {d.itens.map((it) => (
                  <div key={it.opcao} style={{ marginBottom: '10px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', color: '#475569', marginBottom: '4px' }}>
                      <span>{it.opcao}</span>
                      <span style={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>{it.qtd} ({it.pct}%)</span>
                    </div>
                    <div style={{ height: '6px', backgroundColor: '#f1f5f9', borderRadius: '4px', overflow: 'hidden' }}>
                      <div style={{ width: `${it.pct}%`, height: '100%', backgroundColor: '#e11d48' }} />
                    </div>
                  </div>
                ))}
              </div>
            ))}
          </div>

          {/* FEEDBACKS (sugestões) */}
          <div style={{ backgroundColor: '#fff', border: '1px solid #e2e8f0', borderRadius: '16px', padding: '24px' }}>
            <h3 style={{ margin: '0 0 4px 0', fontSize: '16px', fontWeight: 700, color: '#0f172a' }}>Sugestões</h3>
            <p style={{ margin: '0 0 18px 0', fontSize: '13px', color: '#64748b' }}>Respostas de texto livre ({feedbacks.length}).</p>
            {feedbacks.length === 0 ? (
              <p style={{ color: '#94a3b8', fontSize: '14px', margin: 0 }}>Nenhuma sugestão preenchida no período.</p>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                {feedbacks.map((r, index) => (
                  <div key={index} style={{ border: '1px solid #f1f5f9', borderRadius: '12px', padding: '16px' }}>
                    {/* Contexto sem cargo e só com o período aaaa-mm (2026-07-17): esconder
                        cargo e o dia reforça o anonimato agora que o Gerente também acessa. */}
                    <div style={{ fontSize: '12px', color: '#94a3b8', marginBottom: '8px' }}>{[r.LOJA, (r.DATA || '').slice(0, 7)].filter(Boolean).join(' · ') || '—'}</div>
                    {CAMPOS_TEXTO.map((c) => {
                      const valor = (r[c.id] || '').trim();
                      if (!valor) return null;
                      return <div key={c.id} style={{ fontSize: '14px', color: '#334155', whiteSpace: 'pre-wrap', lineHeight: 1.5 }}>{valor}</div>;
                    })}
                  </div>
                ))}
              </div>
            )}
          </div>

        </div>
      </div>
    </div>
  );
}

export default Primeiro15Relatorio;
